import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ShieldCheck, User, Star, Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function UmfrageAntwortPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [antworten, setAntworten] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [fertig, setFertig] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/surveys/${id}`);
        setSurvey(data);
        // Initialwerte
        const init = {};
        for (const f of data.fragen || []) {
          if (f.typ === "multi") init[f.id] = [];
          else if (f.typ === "jaNein") init[f.id] = null;
          else init[f.id] = "";
        }
        setAntworten(init);
      } catch (e) { toast.error(errMsg(e)); nav("/portal"); }
      finally { setLoading(false); }
    })();
  }, [id, nav]);

  const setAntwort = (fid, val) => setAntworten((a) => ({ ...a, [fid]: val }));
  const toggleMulti = (fid, opt) => setAntworten((a) => {
    const cur = a[fid] || [];
    return { ...a, [fid]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });

  const validate = () => {
    for (const f of survey.fragen) {
      const v = antworten[f.id];
      if (!f.pflicht) continue;
      if (f.typ === "multi") {
        if (!Array.isArray(v) || v.length === 0)
          return `Bitte '${f.frage}' beantworten.`;
      } else if (f.typ === "jaNein") {
        if (v !== true && v !== false) return `Bitte '${f.frage}' beantworten.`;
      } else if (f.typ === "skala5") {
        if (!v || v < 1) return `Bitte '${f.frage}' bewerten.`;
      } else if (v === "" || v === null || v === undefined) {
        return `Bitte '${f.frage}' beantworten.`;
      }
    }
    return null;
  };

  const absenden = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSending(true);
    try {
      const payload = survey.fragen
        .filter((f) => antworten[f.id] !== "" && antworten[f.id] !== null && antworten[f.id] !== undefined && !(Array.isArray(antworten[f.id]) && antworten[f.id].length === 0))
        .map((f) => ({ frage_id: f.id, typ: f.typ, wert: antworten[f.id] }));
      await api.post(`/surveys/${id}/antworten`, { antworten: payload });
      setFertig(true);
      toast.success("Vielen Dank für Ihre Antwort!");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  if (!survey) return null;

  if (fertig) {
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <h2 className="text-xl font-semibold">Vielen Dank!</h2>
            <p className="text-sm text-muted-foreground">
              Ihre Antwort wurde erfolgreich übermittelt.
              {survey.modus === "anonym" && " Ihre Angaben sind anonym und lassen keine Rückschlüsse auf Sie zu."}
            </p>
            <Button onClick={() => nav("/portal")}>Zurück zum Portal</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => nav("/portal")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle>{survey.titel}</CardTitle>
            {survey.modus === "anonym"
              ? <Badge variant="outline"><ShieldCheck className="h-3 w-3 mr-1" /> anonym</Badge>
              : <Badge variant="outline"><User className="h-3 w-3 mr-1" /> namentlich</Badge>}
          </div>
          {survey.beschreibung && (
            <p className="text-sm text-muted-foreground">{survey.beschreibung}</p>
          )}
          {survey.deadline && (
            <p className="text-xs text-muted-foreground">Deadline: {fmtDate(survey.deadline)}</p>
          )}
          {survey.modus === "anonym" && (
            <div className="rounded-md border bg-primary/5 p-3 text-xs">
              <ShieldCheck className="h-3.5 w-3.5 inline mr-1 text-primary" />
              <strong>Anonyme Umfrage.</strong> Ihre Antwort wird ohne Namen und Liefernummer gespeichert.
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {survey.fragen.map((f, idx) => (
            <div key={f.id} className="rounded-md border p-3 space-y-2">
              <Label className="text-sm font-medium">
                {idx + 1}. {f.frage}
                {f.pflicht && <span className="text-destructive ml-1">*</span>}
              </Label>
              <FrageInput frage={f} value={antworten[f.id]}
                onChange={(v) => setAntwort(f.id, v)}
                onToggle={(opt) => toggleMulti(f.id, opt)} />
            </div>
          ))}
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button size="lg" onClick={absenden} disabled={sending}
          data-testid="umfrage-absenden">
          {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Antworten absenden
        </Button>
      </div>
    </div>
  );
}

const FrageInput = ({ frage, value, onChange, onToggle }) => {
  if (frage.typ === "freitext") {
    return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (frage.typ === "single") {
    return (
      <RadioGroup value={value || ""} onValueChange={onChange}>
        {(frage.optionen || []).map((o) => (
          <label key={o} className="flex items-center gap-2 py-1 cursor-pointer">
            <RadioGroupItem value={o} />
            <span className="text-sm">{o}</span>
          </label>
        ))}
      </RadioGroup>
    );
  }
  if (frage.typ === "multi") {
    const cur = value || [];
    return (
      <div className="space-y-1">
        {(frage.optionen || []).map((o) => (
          <label key={o} className="flex items-center gap-2 py-1 cursor-pointer">
            <Checkbox checked={cur.includes(o)} onCheckedChange={() => onToggle(o)} />
            <span className="text-sm">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (frage.typ === "skala5") {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${value >= n ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-primary/20"}`}>
            <Star className="h-4 w-4" fill={value >= n ? "currentColor" : "none"} />
          </button>
        ))}
        <span className="text-sm text-muted-foreground ml-2">{value || "–"} / 5</span>
      </div>
    );
  }
  if (frage.typ === "jaNein") {
    return (
      <div className="flex gap-2">
        <Button size="sm" variant={value === true ? "default" : "outline"}
          onClick={() => onChange(true)}>Ja</Button>
        <Button size="sm" variant={value === false ? "default" : "outline"}
          onClick={() => onChange(false)}>Nein</Button>
      </div>
    );
  }
  if (frage.typ === "zahl") {
    return <Input type="number" min={frage.min ?? undefined} max={frage.max ?? undefined}
      value={value === "" ? "" : value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} />;
  }
  if (frage.typ === "datum") {
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return null;
};
